import { defineStore } from "pinia";
import type { Database, Tables } from "~~/types/supabase";

export const useStripeStore = defineStore("stripeStore", {
  state: () => ({
    initialloadingStripe: true,
    loadingStripe: false,
    /** i18n key, or empty when idle. Translated by the consumer. */
    loadStatus: "",
    transferEnabled: false,
    externalAccountNeeded: true,
    payoutEnabled: false,
    newMethod: true,
    onboardingDialog: false,
    /** What the open onboarding dialog is for, fixed when it opens so its texts don't switch halfway. */
    onboardingPurpose: "payouts" as "payouts" | "card",
    usingStripe: false,
    // Not persisted: the account and person objects carry verification PII
    // (names, dates of birth, addresses) and must not land in localStorage.
    account: null as any,
    persons: [] as any[],
    savingStripe: false,
    /** False until a real account status has been fetched this session. */
    statusLoaded: false,
    // Pinned once resolved so a PostHog rollout change can't move someone
    // between onboarding flows halfway through.
    onboardingVariant: null as "control" | "custom" | null,
  }),
  // Loading flags and the fetched status are deliberately NOT persisted:
  // restoring them means the UI renders last session's answer, then corrects
  // itself a moment later once the real status arrives. That is the flicker.
  persist: {
    pick: [
      "newMethod",
      "usingStripe",
      "onboardingVariant",
      // Not transient: it's the user's intent to be onboarding. Dropping it
      // meant any reload (an auth refresh on tab focus can cause one) closed
      // the dialog out from under them.
      "onboardingDialog",
      "onboardingPurpose",
    ],
  },
  getters: {
    /**
     * Payments already run and only card waits on Stripe: card was requested
     * (see _shared/stripe/cardPayments.ts) and Stripe asks for more before it
     * approves the card capabilities, while transfers and payouts are active.
     */
    onlyCardPending(state): boolean {
      const account = state.account;
      if (account?.metadata?.card_payments !== "requested") return false;
      const caps = account.capabilities ?? {};
      return caps.transfers === "active" && !!account.payouts_enabled &&
        ["card_payments", "ideal_payments", "bancontact_payments"].some((name) => caps[name] !== "active");
    },
  },
  actions: {
    /**
     * The one way into Stripe onboarding, from every place that asks for it:
     * the payout banner, the payments settings (payouts and card details), the
     * event's first steps. The dialog in pages/index/index.vue picks the flow;
     * legacy Express accounts go to Stripe's hosted onboarding instead.
     */
    async openOnboarding() {
      this.onboardingPurpose = this.onlyCardPending ? "card" : "payouts";
      if (this.newMethod) this.onboardingDialog = true;
      else await this.createStripeAccount();
    },
    async createStripeAccount(fields?: string, extra_data?: any) {
      const supabase = useSupabaseClient<Database>();
      const userStore = useUserStore();
      this.loadingStripe = true;
      this.loadStatus = "load_status_collecting";
      const org = userStore.organization;
      if (!org?.stripe_account_id) {
        this.newMethod = true;
        this.loadStatus = "load_status_notifying";
        const { data, error } = await supabase.functions.invoke(
          "stripe-connect",
          {
            body: {
              method: "create_account",
              organization_id: org?.id,
              ...extra_data,
            },
          },
        );
      }

      if (this.newMethod) {
        this.loadStatus = "load_status_onboarding";
        this.loadingStripe = false;
        this.loadStatus = "";
      } else {
        this.loadStatus = "load_status_link";
        const { data: link } = await supabase.functions.invoke(
          "stripe-connect",
          {
            body: {
              method: "account_link",
              organization_id: org?.id,
              fields: fields ?? "currently_due",
              return_url: window.location.href,
            },
          },
        );
        if (link) {
          this.loadStatus = "load_status_redirecting";
          window.location.href = link.url;
        } else {
          this.loadingStripe = false;
          this.loadStatus = "";
        }
      }
    },
    async loginToExpress() {
      const supabase = useSupabaseClient<Database>();
      const userStore = useUserStore();
      const { data } = await supabase.functions.invoke(
        "stripe-connect",
        {
          body: {
            method: "create_login_link",
            organization_id: userStore.organization?.id,
          },
        },
      );

      if (data.url) {
        window.open(
          data.url,
          "_blank", // <- This is what makes it open in a new window.
        );
      }
    },
    invokeConnect(body: Record<string, unknown>) {
      const supabase = useSupabaseClient<Database>();
      const userStore = useUserStore();
      return supabase.functions.invoke("stripe-connect", {
        body: { organization_id: userStore.organization?.id, ...body },
      });
    },
    /** Fetch the account and mirror the flags the rest of the app reads. */
    async loadAccount() {
      const userStore = useUserStore();
      if (!userStore.organization?.stripe_account_id) {
        this.account = null;
        return null;
      }
      const { data: account } = await this.invokeConnect({
        method: "get_account",
      });
      this.applyAccount(account);
      return account;
    },
    applyAccount(account: any) {
      if (!account) return;
      const settled = !account.requirements?.currently_due?.length;
      this.$patch({
        account,
        newMethod: account.type != "express",
        transferEnabled: settled,
        payoutEnabled: settled,
        externalAccountNeeded: account.external_accounts &&
          account.external_accounts.total_count == 0,
        statusLoaded: true,
      });
    },
    /** Write verification fields collected by the custom onboarding form. */
    async patchAccount(payload: Record<string, unknown>) {
      this.savingStripe = true;
      try {
        const { data: account, error } = await this.invokeConnect({
          method: "update_account",
          ...payload,
        });
        if (error) throw error;
        this.applyAccount(account);
        return account;
      } finally {
        this.savingStripe = false;
      }
    },
    async loadPersons() {
      const { data } = await this.invokeConnect({ method: "list_persons" });
      this.persons = data?.data ?? [];
      return this.persons;
    },
    async savePerson(person: Record<string, unknown>, personId?: string) {
      this.savingStripe = true;
      try {
        const { data, error } = await this.invokeConnect({
          method: personId ? "update_person" : "create_person",
          person_id: personId,
          person,
        });
        if (error) throw error;
        await this.loadPersons();
        return data;
      } finally {
        this.savingStripe = false;
      }
    },
    async removePerson(personId: string) {
      this.savingStripe = true;
      try {
        await this.invokeConnect({ method: "delete_person", person_id: personId });
        await this.loadPersons();
      } finally {
        this.savingStripe = false;
      }
    },
    /** Push the IBAN already stored on the organization to Stripe. */
    async saveExternalAccount() {
      this.savingStripe = true;
      try {
        const { error } = await this.invokeConnect({
          method: "create_external_account",
        });
        if (error) throw error;
        await this.loadAccount();
      } finally {
        this.savingStripe = false;
      }
    },
    async refreshAccountStatus() {
      const userStore = useUserStore();
      if (this.usingStripe && userStore.organization?.stripe_account_id) {
        this.loadingStripe = true;
        const supabase = useSupabaseClient<Database>();
        const { data: account } = await supabase.functions.invoke(
          "stripe-connect",
          {
            body: {
              method: "get_account",
              organization_id: userStore.organization?.id,
            },
          },
        );
        this.loadingStripe = false;
        this.initialloadingStripe = false;
        this.applyAccount(account);
      } else {
        this.initialloadingStripe = false;
        this.statusLoaded = true;
        this.newMethod = true;
      }
    },
  },
});
